import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/contexts/LanguageContext";
import { ASSETS } from "@/lib/assets";
import { ArrowRight, Factory, Sparkles, Boxes, Beaker, Store, Users2, Handshake, Lock } from "lucide-react";

export default function TWFH() {
  const { tr } = useLang();

  const services = [
    { no: tr("twfh_service_1_no"), title: tr("twfh_service_1_title"), desc: tr("twfh_service_1_desc"), icon: <Factory size={16} /> },
    { no: tr("twfh_service_2_no"), title: tr("twfh_service_2_title"), desc: tr("twfh_service_2_desc"), icon: <Sparkles size={16} /> },
    { no: tr("twfh_service_3_no"), title: tr("twfh_service_3_title"), desc: tr("twfh_service_3_desc"), icon: <Boxes size={16} /> },
  ];
  const store = [
    { no: tr("twfh_store_1_no"), title: tr("twfh_store_1_title"), desc: tr("twfh_store_1_desc"), icon: <Beaker size={16} /> },
    { no: tr("twfh_store_2_no"), title: tr("twfh_store_2_title"), desc: tr("twfh_store_2_desc"), icon: <Store size={16} /> },
    { no: tr("twfh_store_3_no"), title: tr("twfh_store_3_title"), desc: tr("twfh_store_3_desc"), icon: <Users2 size={16} /> },
    { no: tr("twfh_store_4_no"), title: tr("twfh_store_4_title"), desc: tr("twfh_store_4_desc"), icon: <Handshake size={16} /> },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header transparent />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pfi-dotgrid opacity-30 pointer-events-none" />
        <div className="absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-[#d4af37]/10 blur-[130px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 pfi-fade-up">
            <div className="pfi-overline">{tr("twfh_page_eyebrow")}</div>
            <h1 className="mt-6 font-serif text-5xl lg:text-7xl leading-[1.02] tracking-tight">
              <span className="pfi-gradient-text">TWFH</span>
            </h1>
            <div className="mt-4 font-serif text-lg lg:text-xl text-[#e6c687]">{tr("twfh_page_subtitle")}</div>
            <div className="mt-3 pfi-overline">{tr("twfh_page_position")}</div>
            <p className="mt-8 max-w-2xl text-lg text-[#a9a9b8] leading-relaxed font-light">
              {tr("twfh_page_intro")}
            </p>
          </div>
          <div className="lg:col-span-5 pfi-fade-up">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-[#d4af37]/15 via-transparent to-[#8B5CF6]/10 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden pfi-gold-border bg-black flex items-center justify-center p-12 min-h-[320px]">
                <img
                  src={ASSETS.twfhLogo}
                  alt="TWFH — Tubagus Wijaya Fragrance House"
                  data-testid="twfh-hero-logo"
                  className="max-h-[280px] w-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-overline">{tr("twfh_services_title")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-4xl">Professional Services</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {services.map((s, i) => (
            <div key={i} className="pfi-card p-6 h-full" data-testid={`twfh-service-${i + 1}`}>
              <div className="pfi-overline inline-flex items-center gap-2">{s.icon}<span>{s.no}</span></div>
              <div className="mt-3 font-serif text-xl text-[#f8f8fc]">{s.title}</div>
              <p className="mt-3 text-sm text-[#a9a9b8] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Store */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 pb-16">
        <div className="pfi-overline">{tr("twfh_store_title")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-4xl">Store</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {store.map((s, i) => (
            <div key={i} className="pfi-card p-6 h-full" data-testid={`twfh-store-${i + 1}`}>
              <div className="pfi-overline inline-flex items-center gap-2">{s.icon}<span>{s.no}</span></div>
              <div className="mt-3 font-serif text-xl text-[#f8f8fc]">{s.title}</div>
              <p className="mt-3 text-sm text-[#a9a9b8] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Boundary — Personalized Perfume is not TWFH */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 pb-24">
        <div className="pfi-card p-8 border-[#d4af37]/30" data-testid="twfh-boundary">
          <div className="pfi-overline inline-flex items-center gap-2"><Lock size={12} /> {tr("twfh_boundary_title")}</div>
          <p className="mt-3 text-[#c8c8d4] leading-relaxed max-w-3xl">{tr("twfh_boundary_body")}</p>
          <Link
            to="/perfumery-tech"
            data-testid="twfh-go-perfumery-tech"
            className="mt-6 pfi-btn-outline rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2"
          >
            PERSONA PERFUMERY TECH <ArrowRight size={12} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
